import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';

const endpoint = '/admin/app/notification-permission-prompt';
const empty = {
  enabled: false,
  titleKo: '알림 설정 안내',
  titleEn: 'Notification Settings',
  bodyKo: '댓글과 팔로우 소식, 새로운 앨범과 나의 음악 기록을 알려드릴게요.',
  bodyEn: 'Get comments, follows, new albums, and updates from your music log.',
  disclosureKo: '이벤트 혜택 알림도 설정에서 별도로 선택할 수 있어요',
  disclosureEn:
    'Event and promotional notifications can be selected separately in notification settings.',
};

export default function NotificationPermissionPromptManagement() {
  const [form, setForm] = useState(empty);
  const [startupPopupEnabled, setStartupPopupEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const draft = useAdminDraftGuard(form, { enabled: !loading && !loadFailed, busy });

  useEffect(() => {
    let live = true;
    setLoading(true);
    setLoadFailed(false);
    setError('');
    void Promise.all([api.getAuth(endpoint), api.getAuth('/admin/ads/startup-popup/items')])
      .then(([prompt, popups]) => {
        if (!live) return;
        setForm({ ...empty, ...prompt.data.data });
        const now = Date.now();
        setStartupPopupEnabled(
          Boolean(
            popups.data.data?.some(
              (item) =>
                item.enabled &&
                (!item.startsAt || Date.parse(item.startsAt) <= now) &&
                (!item.endsAt || now < Date.parse(item.endsAt)),
            ),
          ),
        );
      })
      .catch(() => {
        if (live) {
          setLoadFailed(true);
          setError('알림 권한 안내와 현재 첫 접속 팝업 상태를 불러오지 못했습니다.');
        }
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [reloadKey]);

  const change = (key, value) => setForm((current) => ({ ...current, [key]: value }));
  const save = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    setSaved(false);
    const payload = Object.fromEntries(
      Object.entries(form).map(([key, value]) =>
        typeof value === 'string' ? [key, value.trim()] : [key, value],
      ),
    );
    try {
      if (
        !payload.titleKo ||
        !payload.titleEn ||
        !payload.bodyKo ||
        !payload.bodyEn ||
        !payload.disclosureKo ||
        !payload.disclosureEn
      )
        throw new Error('한글·영문 제목, 본문, 보조 안내를 모두 입력해주세요.');
      const { data } = await api.putAuth(endpoint, payload);
      setForm({ ...empty, ...data.data });
      draft.markClean({ ...empty, ...data.data });
      setSaved(true);
    } catch (failure) {
      setError(failure.response?.data?.msg || failure.message || '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <Typography>설정을 불러오는 중…</Typography>;
  if (loadFailed) {
    return (
      <Alert
        severity="error"
        action={<Button onClick={() => setReloadKey((value) => value + 1)}>재시도</Button>}
      >
        {error}
      </Alert>
    );
  }
  return (
    <Stack spacing={2}>
      <Typography variant="h6">알림 권한 안내</Typography>
      <Alert severity="info">
        로그인 회원의 기기에서 한 번만 표시됩니다. ‘알림 받을게요’를 누르면 앱 설정 화면으로
        이동하지 않고 OS 알림 권한을 바로 요청합니다. 이벤트·혜택 알림은 자동으로 켜지지 않습니다.
      </Alert>
      {form.enabled && startupPopupEnabled && (
        <Alert severity="warning">
          현재 첫 접속 팝업도 공개 중입니다. 두 안내는 같은 화면에 동시에 겹쳐 표시됩니다.
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      <Box component="form" aria-label="알림 권한 안내 편집" onSubmit={save}>
        <Stack spacing={2} component="fieldset" disabled={busy} sx={{ border: 0, p: 0, m: 0 }}>
          <FormControlLabel
            label="앱에 안내 표시"
            control={
              <Checkbox
                checked={form.enabled}
                onChange={(event) => change('enabled', event.target.checked)}
              />
            }
          />
          <TextField
            required
            label="제목 (한국어)"
            value={form.titleKo}
            inputProps={{ maxLength: 100 }}
            onChange={(event) => change('titleKo', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="본문 (한국어)"
            value={form.bodyKo}
            inputProps={{ maxLength: 300 }}
            onChange={(event) => change('bodyKo', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="보조 안내 (한국어)"
            value={form.disclosureKo}
            inputProps={{ maxLength: 240 }}
            onChange={(event) => change('disclosureKo', event.target.value)}
          />
          <TextField
            required
            label="제목 (영어)"
            value={form.titleEn}
            inputProps={{ maxLength: 100 }}
            onChange={(event) => change('titleEn', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="본문 (영어)"
            value={form.bodyEn}
            inputProps={{ maxLength: 300 }}
            onChange={(event) => change('bodyEn', event.target.value)}
          />
          <TextField
            required
            multiline
            minRows={2}
            label="보조 안내 (영어)"
            value={form.disclosureEn}
            inputProps={{ maxLength: 240 }}
            onChange={(event) => change('disclosureEn', event.target.value)}
          />
          <Typography variant="subtitle1">한국어 미리보기</Typography>
          <Paper variant="outlined" sx={{ maxWidth: 390, borderRadius: 3, overflow: 'hidden' }}>
            <Stack spacing={2} sx={{ p: 3 }}>
              <Typography fontSize={19} fontWeight={700} lineHeight="26px">
                {form.titleKo}
              </Typography>
              <Typography sx={{ fontSize: 15, lineHeight: '23px', whiteSpace: 'pre-wrap' }}>
                {form.bodyKo}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                ({form.disclosureKo.replace(/[.。]\s*$/, '').replace(/^\((.*)\)$/, '$1')})
              </Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                borderTop: 1,
                borderColor: 'divider',
              }}
            >
              <Button
                type="button"
                tabIndex={-1}
                disableRipple
                sx={{ borderRadius: 0, color: 'text.secondary' }}
              >
                나중에
              </Button>
              <Button type="button" tabIndex={-1} disableRipple sx={{ borderRadius: 0 }}>
                알림 받을게요
              </Button>
            </Box>
          </Paper>
          {saved && <Alert severity="success">저장했습니다.</Alert>}
          <Button type="submit" variant="contained">
            저장
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
